if (!customElements.get('image-first-hero')) {
  customElements.define('image-first-hero', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      const options = { signal: this.controller.signal };
      this.video = this.querySelector('video');
      this.poster = this.querySelector('.adr-new-video-hero__poster');
      this.pauseButton = this.querySelector('.adr-new-video-hero__pause');
      this.mobile = matchMedia('(max-width: 749px)');
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.generation = 0;
      this.visible = false;
      this.pausedByUser = false;
      this.video.addEventListener('playing', () => {
        const reveal = () => {
          if (!this.isConnected || this.video.paused) return;
          this.classList.add('is-playing');
          this.pauseButton.hidden = this.pauseButton.dataset.enabled === 'false';
          this.pauseButton.textContent = 'Pause video';
        };
        if (this.video.requestVideoFrameCallback) this.video.requestVideoFrameCallback(reveal);
        else requestAnimationFrame(reveal);
      }, options);
      this.video.addEventListener('error', () => {
        this.classList.remove('is-playing');
        this.pauseButton.hidden = true;
      }, options);
      this.pauseButton.addEventListener('click', () => {
        this.pausedByUser = !this.video.paused;
        if (this.pausedByUser) {
          this.video.pause();
          this.pauseButton.textContent = 'Play video';
        } else this.play();
      }, options);
      this.mobile.addEventListener('change', () => this.prepare(), options);
      this.reducedMotion.addEventListener('change', () => this.prepare(), options);
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) this.video.pause();
        else if (this.visible && !this.pausedByUser) this.play();
      }, options);
      this.observer = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting;
        if (this.visible) this.prepare();
        else this.video.pause();
      });
      this.observer.observe(this);

      // Reuse the header's existing search UI and suggestion provider.
      this.querySelector('form')?.addEventListener('submit', event => {
        const trigger = [...document.querySelectorAll('.js-search-header')]
          .find(element => element.getClientRects().length);
        const input = document.querySelector('predictive-search[data-context="header"] input[type="search"]');
        if (!trigger || !input) return; // The normal search-results form remains the fallback.
        event.preventDefault();
        trigger.click();
        input.value = this.querySelector('input[name="q"]').value;
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }, options);
    }

    async prepare() {
      const generation = ++this.generation;
      const source = this.mobile.matches
        ? (this.dataset.mobileVideo || this.dataset.desktopVideo)
        : this.dataset.desktopVideo;
      if (this.reducedMotion.matches || navigator.connection?.saveData || !source) {
        this.video.pause();
        this.video.removeAttribute('src');
        this.video.load();
        this.classList.remove('is-playing');
        this.pauseButton.hidden = true;
        return;
      }
      if (this.video.getAttribute('src') === source) {
        if (!this.pausedByUser) this.play();
        return;
      }
      this.video.pause();
      this.classList.remove('is-playing');
      this.pauseButton.hidden = true;
      // Let the responsive image download and paint before fetching video bytes.
      if (this.poster) {
        try { await this.poster.decode(); } catch (_) { /* Keep the background fallback. */ }
      }
      await new Promise(resolve => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      if (generation !== this.generation || !this.isConnected || !this.visible) return;
      this.video.muted = true;
      this.video.src = source;
      this.video.load();
      if (!this.pausedByUser) this.play();
    }

    play() {
      if (!this.visible || !this.video.getAttribute('src') || document.hidden || this.reducedMotion.matches || navigator.connection?.saveData) return;
      this.video.play().catch(() => {
        // Autoplay may be blocked in low-power mode. Keep the image visible.
        this.pauseButton.hidden = this.pauseButton.dataset.enabled === 'false';
        this.pauseButton.textContent = 'Play video';
      });
    }

    disconnectedCallback() {
      ++this.generation;
      this.controller.abort();
      this.observer.disconnect();
      this.video.pause();
      this.video.removeAttribute('src');
      this.video.load();
    }
  });
}
